import React, { FormEvent, useEffect, useState } from 'react';

import gql from 'graphql-tag';
import { useLazyQuery, useMutation } from 'react-apollo';
import styled from 'styled-components';

import * as actions from 'actions';
import VenuePicker from 'Components/VenuePicker';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useDispatch, useI18n } from 'Hooks';
import { Box } from 'styles/box';

const VENUE_QUERY = gql`
  query GetVenue($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      beeperList
    }
  }
`;

const UPDATE_VENUE_METADATA = gql`
  mutation updateVenueMetadata($input: UpdateVenueMetadataInput!) {
    updateVenueMetadata(input: $input) {
      success
      error
      message
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  width: 850px;
  padding: 30px;
`;

const VenueContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 400px;
  margin: 20px 0px 40px 0px;
`;

const FormContainer = styled.form`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
  height: 40px;
`;

const Input = styled.input`
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  width: 200px;
`;

const Button = styled.button`
  padding: 4px 8px;
  font-weight: bold;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  border: none;
  cursor: pointer;
`;

const DeleteButton = styled(Button)`
  background-color: #c70000;
`;

const BoldParagraph = styled(Paragraph)`
  font-weight: bold;
`;

const BeepersContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  width: 850px;
`;

const BeeperCard = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background-color: #fff;
  border-radius: 4px;
  border: 1px solid #ccc;
  width: 200px;
`;

const BeeperName = styled.p`
  font-weight: bold;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #ccc;
  margin: 20px 0px 30px 0px;
  width: 830px;
`;

const Beepers: React.FC = () => {
  const { t } = useI18n();

  const [newBeeper, setNewBeeper] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [venueId, setVenueId] = useState<string>('');
  const [beepers, setBeepers] = useState<string[]>([]);

  const [updateVenueMetadata] = useMutation(UPDATE_VENUE_METADATA);

  const dispatch = useDispatch();

  const [getVenueData, { data: venueData, loading: venueDataLoading, refetch: refetchVenueData }] =
    useLazyQuery(VENUE_QUERY, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (venueId) {
      getVenueData({
        variables: {
          venueId,
        },
      });
    }
  }, [venueId]);

  useEffect(() => {
    if (venueData?.venue?.beeperList) {
      setBeepers(venueData.venue.beeperList);
    }
  }, [venueData]);

  const addBeeperHandler = async (e: FormEvent) => {
    e.preventDefault();

    if (newBeeper === '') {
      setError(t('Beepers.AddBeeper.Error.Empty'));
      return;
    }

    if (beepers.includes(newBeeper)) {
      setError(t('Beepers.AddBeeper.Error.AlreadyExists'));
      return;
    }

    try {
      await updateVenueMetadata({
        variables: {
          input: {
            venueId,
            beeperList: [...beepers, newBeeper],
          },
        },
      });
      dispatch(actions.showNotice(`${newBeeper} ${t('Beepers.WasAdded')}`, 3000));
      setNewBeeper('');
      refetchVenueData();
    } catch (error) {
      console.error('Error updating venue metadata:', error);
    }
  };

  const deleteBeeperHandler = async (beeper: string) => {
    try {
      await updateVenueMetadata({
        variables: {
          input: {
            venueId,
            beeperList: beepers.filter((i) => i !== beeper),
          },
        },
      });
      dispatch(actions.showNotice(`Deleted beeper ${beeper}`, 3000));
      refetchVenueData();
    } catch (error) {
      console.error('Error updating venue metadata:', error);
    }
  };

  return (
    <Container>
      <Heading level={1}>{t('Beepers.Title')}</Heading>
      <VenueContainer>
        <Paragraph level={4} bottom={0}>
          {t('Beepers.SelectVenue')}
        </Paragraph>
        <VenuePicker onIDChange={(id) => setVenueId(id)} />
      </VenueContainer>
      <BoldParagraph level={4} bottom={0}>{`Added beepers (${
        beepers?.length || 0
      })`}</BoldParagraph>
      <FormContainer onSubmit={addBeeperHandler}>
        <Input
          type="text"
          placeholder="Numeric only"
          value={newBeeper}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            // Allow only numbers and limit to 3 digits - this is because the easyId in OM is three number like this #000
            if (/^\d{0,3}$/.test(value)) {
              setNewBeeper(value);
            }

            if (error) {
              setError('');
            }
          }}
        />
        <Button type="submit">{t('Beepers.AddBeeper')}</Button>
        {error && (
          <Box display="flex" alignItems="center">
            <Paragraph level={4} color="var(--colorUIRed)">
              {error}
            </Paragraph>
          </Box>
        )}
      </FormContainer>
      <Divider />
      <BeepersContainer>
        {venueData?.venue?.beeperList?.map((beeper) => (
          <BeeperCard>
            <BeeperName>{beeper}</BeeperName>
            <DeleteButton onClick={() => deleteBeeperHandler(beeper)}>X</DeleteButton>
          </BeeperCard>
        ))}
      </BeepersContainer>
    </Container>
  );
};

export default Beepers;
